<template>
    <div style="position: relative">
        <div ref="map" id="ol-map-container" class="w-100" :style="{height: mapHeight}"></div>
        <div v-if="tooltip" v-html="tooltipHtml"
             class="map-tooltip" :style="{left: tooltipX + 'px', top: tooltipY + 'px'}">
        </div>
        <div class="map-menu-bar">
            <div ref="search" class="search-input">
                <input ref="searchInput" class="form-control" v-model="searchInput" @focus="showSearchResult = true">
                <font-awesome-icon icon="search" size="lg" color="dimgray" class="search-icon"/>
                <font-awesome-icon icon="times" size="lg" class="clear-icon"
                                   :class="{'no-pointer-events': !canClearSearch}"
                                   @click="searchClear()" :color="canClearSearch ? 'blue' : 'lightgray'"/>
                <div class="dropdown-menu w-100" style="top: unset" :class="{show: showSearchOptions}">
                    <div v-if="!searching && searchResultKey === searchInput && !searchResult.length"
                         class="text-center">
                        没有匹配的标绘
                    </div>
                    <template v-else>
                        <template v-if="searchResult.length && searchResultKey === searchInput">
                            <a class="dropdown-item d-flex align-items-center justify-content-between"
                               href="javascript:void(0)" v-for="item in searchResult" @click="selectSearchResult(item)">
                                <div v-html="highlightText(item.displayName, item.keyword)"></div>
                                <div class="text-black-50 form-inline">
                                    <div>{{item.type}}</div>
                                </div>
                            </a>
                        </template>
                        <div v-else class="text-center w-100">
                            <b-spinner small label="Small Spinner"></b-spinner>
                        </div>
                    </template>
                </div>
            </div>
            <div ref="route" class="route-panel">
                <div ref="startRoute" class="flex-1 dropdown" style="max-width: 120px">
                    <input :class="startError ? 'text-danger' : ''" ref="routeStartInput" placeholder="输入始发港"
                           class="form-control route-input" v-model="startText" @focus="focusStart()">
                    <div class="dropdown-menu route-dropdown" :class="{show: showStartItems && startItems.length > 0}">
                        <a class="dropdown-item" href="javascript:void(0)" v-for="item in startItems"
                           @click="selectStartItem(item)">
                            <div class="d-flex align-items-center" v-text="item"></div>
                        </a>
                    </div>
                    <div class="dropdown-menu" :class="{show: showStartItems && startItems.length === 0}">
                        <a class="dropdown-item" href="javascript:void(0)">
                            <div class="d-flex align-items-center">
                                没有找到“<span v-text="startText" class="text-danger"></span>”
                            </div>
                        </a>
                    </div>
                </div>
                —
                <div ref="targetRoute" class="flex-1 dropdown mr-1" style="max-width: 120px">
                    <input :class="targetError ? 'text-danger' : ''" ref="routeTargetInput" placeholder="输入目的港"
                           class="form-control route-input" v-model="targetText" @focus="focusTarget()">
                    <div class="dropdown-menu route-dropdown"
                         :class="{show: showTargetItems && targetItems.length > 0}">
                        <a class="dropdown-item" href="javascript:void(0)" v-for="item in targetItems"
                           @click="selectTargetItem(item)">
                            <div class="d-flex align-items-center" v-text="item"></div>
                        </a>
                    </div>
                    <div style="left: 196px;" class="dropdown-menu"
                         :class="{show: showTargetItems && targetItems.length === 0}">
                        <a class="dropdown-item" href="javascript:void(0)">
                            <div class="d-flex align-items-center">
                                没有找到“<span v-text="targetText" class="text-danger"></span>”
                            </div>
                        </a>
                    </div>
                </div>
                <input type="number" v-model="maxWeight" class="form-control flex-1 mr-1"
                       style="max-width: 120px" placeholder="输入吨位"/>
                <div class="flex-1" style="max-width: 220px" v-if="mode !== 'train'">
                    <multi-port-selector type="throughPositions" v-model="waypoints" placeholder="输入途径点"/>
                </div>
                <button class="btn btn-primary ml-2" @click="queryRoute()">查看</button>
                <button class="btn btn-secondary ml-1" @click="clearRoute()">清空</button>
            </div>
            <div class="config-buttons">
                <b-dropdown class="marker-dropdown" block variant="primary" menu-class="w-100" :text="markerTypesDesc">
                    <b-dropdown-form v-for="item in markerTypes" :key="item.name"
                                     @click="clickMarkerType($event, item)" @change="markerTypeChange(item)">
                        <b-form-checkbox v-model="item.value">{{item.name}}</b-form-checkbox>
                    </b-dropdown-form>
                </b-dropdown>
            </div>
        </div>
    </div>
</template>

<script>
import * as $ from "jquery";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {fromLonLat, toLonLat} from "@/olMap/coordUtils";
import OpenLayersMap from "ol/Map";
import {defaults as defaultInteractions} from "ol/interaction";
import {defaults as defaultControls} from "ol/control";
import ScaleLineControl from "@/olMap/control/ScaleLine";
import MapTypeControl from "@/olMap/control/MapType";
import RoadTypeControl from "@/olMap/control/RoadType";
import View from "ol/View";
import {getLayers} from "@/olMap/mapLayers";
import {
    createFeature, createGroupStyle, createPlotStyle, getLabelItem, getLabelRect, getPlotLabelItem, initPlotTypes,
    labelDirections, placeLabelItems, selectedStyle, plotDisplayName, vesselDisplayName, linePlotDisplayName,
    rectPlotDisplayName, getRectCoordinates,
} from "@/olMap/featureUtils";
import Practice from "@/services/practice";
import Port from "@/services/port";
import Plot from "@/services/plot";
import * as Html from "@/utils/html";
import * as MsgBox from "@/utils/msgBox";
import Feature from "ol/Feature";
import {Polygon} from "ol/geom";
import {Fill, Icon, Stroke, Style, Text} from "ol/style";
import * as olExtent from "ol/extent";
import _ from "lodash";
import MultiPortSelector from "@/components/MultiPortSelector.vue";

export default {
    name: "PracticeMap",
    components: {MultiPortSelector},
    data() {
        return {
            mapHeight: "100vh",
            tooltip: "",
            tooltipX: 0,
            tooltipY: 0,
            searchInput: null,
            searching: 0,
            showSearchResult: false,
            selectedItem: null,
            searchResultKey: null,
            searchResult: [],
            startError: false,
            targetError: false,
            startText: "",
            targetText: "",
            maxWeight: null,
            waypoints: [],
            startItems: [],
            targetItems: [],
            showStartItems: false,
            showTargetItems: false,
            markerTypes: [{name: "船闸", value: true}, {name: "地标", value: true}],
            mode: "",
        };
    },
    computed: {
        canClearSearch() {
            return this.searchInput || this.selectedItem;
        },
        tooltipHtml() {
            return Html.format(this.tooltip);
        },
        showSearchOptions() {
            return this.showSearchResult && (this.searching || this.searchResult.length > 0 || this.searchInput);
        },
        highlightText(text, keyword) {
            return (text, keyword) => Html.highlight(text, keyword);
        },
        markerTypesDesc() {
            let names = this.showingMarkerTypes();
            if (names.length > 0) {
                return "显示" + names.join("，");
            } else {
                return "不显示标绘";
            }
        },
    },
    methods: {
        getClientSize() {
            return [
                window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
                window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
            ];
        },
        showingMarkerTypes() {
            return this.markerTypes.filter(x => x.value).map(x => x.name);
        },
        clickMarkerType(e, item) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    item.value = !item.value;
                    this.markerTypeChange(item);
                }
            }
        },
        markerTypeChange(item) {
            this.updatePlots();
        },
        focusStart: async function () {
            await this.queryStartItems();
            this.showStartItems = true;
        },
        focusTarget: async function () {
            await this.queryTargetItems();
            this.showTargetItems = true;
        },
        queryStartItems: async function () {
            this.startItems = await Port.multiWords(this.startText);
        },
        queryTargetItems: async function () {
            this.targetItems = await Port.multiWords(this.targetText);
        },
        selectStartItem: function (item) {
            this.showStartItems = false;
            this.startText = item;
            this.$refs.routeStartInput.blur();
        },
        selectTargetItem: function (item) {
            this.showTargetItems = false;
            this.targetText = item;
            this.$refs.routeTargetInput.blur();
        },
        clearRoute: function () {
            this.startText = "";
            this.targetText = "";
            this.maxWeight = null;
            this.waypoints = [];
            this.setVesselRoute([], [], []);
        },
        queryRoute: async function () {
            if (!this.startText) {
                MsgBox.alert(this, "请填写始发港");
                return;
            }
            if (!this.targetText) {
                MsgBox.alert(this, "请填写目的港");
                return;
            }

            this.startError = false;
            this.targetError = false;
            let route = await Plot.route(this.startText, this.targetText, this.maxWeight, this.waypoints.join());
            this.startError = route.startError;
            this.targetError = route.targetError;
            this.setVesselRoute(route.plots, route.rects, route.throughPlots);
        },
        updateGroups() {
            const groupLimits = [
                {radius: 55, minCount: 200},
                {radius: 50, minCount: 100},
                {radius: 45, minCount: 50},
                {radius: 40, minCount: 20},
                {radius: 35, minCount: 5},
            ];
            const maxRadius = groupLimits.reduce((r, x) => Math.max(r, x.radius), 0);
            const getPixels = plots => {
                if (plots.length > 0) {
                    let minX = Number.MAX_SAFE_INTEGER;
                    let minY = Number.MAX_SAFE_INTEGER;
                    let maxX = Number.MIN_SAFE_INTEGER;
                    let maxY = Number.MIN_SAFE_INTEGER;
                    for (let p of plots) {
                        p.px = Math.floor(p.x);
                        p.py = Math.floor(p.y);
                        minX = Math.min(minX, p.px);
                        minY = Math.min(minY, p.py);
                        maxX = Math.max(maxX, p.px);
                        maxY = Math.max(maxY, p.py);
                    }
                    minX -= maxRadius;
                    minY -= maxRadius;
                    maxX += maxRadius;
                    maxY += maxRadius;
                    let width = maxX + 1 - minX;
                    let height = maxY + 1 - minY;
                    let pixels = new Array(width * height).fill(null);
                    plots.forEach(p => {
                        p.px -= minX;
                        p.py -= minY;
                        let pi = p.px + p.py * width;
                        if (pixels[pi]) {
                            pixels[pi].push(p);
                        } else {
                            pixels[pi] = [p];
                        }
                    });
                    return {width, height, pixels, minX, minY};
                } else {
                    return null;
                }
            };
            const getGroupsByDelta = (pixels, groupParams) => {
                let minX = Number.MAX_SAFE_INTEGER;
                let maxX = Number.MIN_SAFE_INTEGER;
                let minY = Number.MAX_SAFE_INTEGER;
                let maxY = Number.MIN_SAFE_INTEGER;
                let groups = [];
                groupParams.forEach(p => {
                    minX = Math.min(minX, p.minX);
                    maxX = Math.max(maxX, p.maxX);
                    minY = Math.min(minY, p.minY);
                    maxY = Math.max(maxY, p.maxY);
                    groups.push(Object.assign({x: 0, y: 0, points: [], count: 0}, p));
                });
                for (let y = minY; y < maxY; y++) {
                    let lineStart = y * pixels.width;
                    for (let x = minX; x < maxX; x++) {
                        let pi = lineStart + x;
                        let pxs = pixels.pixels[pi];
                        if (pxs) {
                            groups.forEach(group => {
                                if (x >= group.minX && x < group.maxX && y >= group.minY && y < group.maxY) {
                                    group.count += pxs.length;
                                    group.points.push(pi);
                                    group.x += x * pxs.length;
                                    group.y += y * pxs.length;
                                }
                            })
                        }
                    }
                }
                groups.forEach(group => {
                    group.x /= group.count;
                    group.y /= group.count;
                });
                return groups;
            };
            const getGroupByDelta = (pixels, radius, minPlots, minX, maxX, minY, maxY) => {
                let group = {x: 0, y: 0, points: [], count: 0, radius, minPlots};
                for (let y = minY; y < maxY; y++) {
                    let lineStart = y * pixels.width;
                    for (let x = minX; x < maxX; x++) {
                        let pi = lineStart + x;
                        let pxs = pixels.pixels[pi];
                        if (pxs) {
                            group.count += pxs.length;
                            group.points.push(pi);
                            group.x += x * pxs.length;
                            group.y += y * pxs.length;
                        }
                    }
                }
                group.x /= group.count;
                group.y /= group.count;
                return group;
            };
            const getGroup = (pixels, x, y) => {
                let groups = getGroupsByDelta(pixels, groupLimits.map(p => Object.assign({}, p, {
                    minX: x - p.radius, maxX: x + p.radius, minY: y, maxY: y + p.radius * 2
                })));
                for (let group of groups) {
                    if (group.count >= group.minCount) {
                        return group;
                    }
                }
                let item = groups[groups.length - 1];
                let cx = Math.round(item.x);
                let cy = Math.round(item.y);
                let r = item.radius;
                let group = getGroupByDelta(pixels, r, item.minCount, cx - r, cx + r, cy - r, cy + r);
                if (group.count >= group.minCount) {
                    return group;
                }
                return null;
            };
            const getDistance = (a, b) => {
                let dx = a.x - b.x;
                let dy = a.y - b.y;
                return dx * dx + dy * dy;
            };
            const combineGroups = groups => {
                while (true) {
                    let combinedSet = new Set();
                    let newGroups = [];
                    for (let i = 0; i < groups.length - 1; i++) {
                        if (!combinedSet.has(i)) {
                            let groupA = groups[i];
                            for (let j = i + 1; j < groups.length; j++) {
                                if (!combinedSet.has(j)) {
                                    let groupB = groups[j];
                                    let minDistance = Math.max(groupA.radius, groupB.radius) * 0.7;
                                    if (getDistance(groupA, groupB) <= minDistance * minDistance) {
                                        let count = groupA.plots.length + groupB.plots.length;
                                        newGroups.push({
                                            x: (groupA.x * groupA.plots.length + groupB.x * groupB.plots.length) / count,
                                            y: (groupA.y * groupA.plots.length + groupB.y * groupB.plots.length) / count,
                                            plots: groupA.plots.concat(groupB.plots),
                                            radius: Math.max(groupA.radius, groupB.radius),
                                        });
                                        combinedSet.add(i);
                                        combinedSet.add(j);
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    if (newGroups.length > 0) {
                        groups = groups.filter((x, i) => !combinedSet.has(i)).concat(newGroups);
                    } else {
                        return groups;
                    }
                }
            };
            const extractGroup = (pixels, group) => {
                let result = {x: group.x + pixels.minX, y: group.y + pixels.minY, plots: [], radius: group.radius};
                group.points.forEach(pi => {
                    result.plots = result.plots.concat(pixels.pixels[pi]);
                    pixels.pixels[pi] = null;
                });
                return result;
            };

            let view = this.map.getView();
            let extent = view.calculateExtent(this.map.getSize());
            let typeSet = new Set(this.showingMarkerTypes());
            let plots = this.allPlots
                .filter(p => typeSet.has(p.type) && olExtent.containsCoordinate(extent, p.coordinate))
                .map(x => Object.assign({}, x));
            let groups = [];
            let zoom = view.getZoom();
            if (zoom <= 13) {
                let resolution = view.getResolutionForZoom(zoom);
                plots.forEach(p => {
                    p.x = p.coordinate[0] / resolution;
                    p.y = p.coordinate[1] / resolution;
                })
                let pixels = getPixels(plots);
                if (pixels) {
                    let groupIds = [];
                    let width = pixels.width - maxRadius;
                    let height = pixels.height - maxRadius;
                    for (let y = maxRadius; y < height; y++) {
                        for (let x = maxRadius; x < width; x++) {
                            if (pixels.pixels[x + y * pixels.width]) {
                                let group = getGroup(pixels, x, y);
                                if (group) {
                                    let extractedGroup = extractGroup(pixels, group);
                                    groups.push(extractedGroup);
                                    groupIds = groupIds.concat(extractedGroup.plots.map(p => p.id));
                                }
                            }
                        }
                    }
                    if (groupIds.length > 0) {
                        let groupIdSet = new Set(groupIds);
                        plots = plots.filter(p => !groupIdSet.has(p.id));
                    }
                }
                groups = combineGroups(groups);
                groups.forEach((g, index) => {
                    g.coordinate = [Math.round(g.x * resolution), Math.round(g.y * resolution)];
                    g.size = g.plots.length;
                    g.id = index + 1;
                });
            }

            this.plots = plots;
            this.groups = groups;
        },
        updatePlots() {
            this.updateGroups();
            this.updateGroupLayer();
            this.updatePlotLayer();
        },
        updateGroupLayer() {
            let features = this.groups.map(g => this.createGroupFeature(g));
            this.groupLayer.setSource(new VectorSource({features: features}));
        },
        isSelectedPlot(plot) {
            return this.selectedItem && this.selectedItem.class === "plot"
                && (plot.id === this.selectedItem.id || plot.name === this.selectedItem.name);
        },
        updatePlotLayer() {
            let occupiedRects = (this.selectedOccupiedRects || []).slice();
            let plotLabelItemsCursor = [];
            let plotLabelItemsNormal = [];
            this.plots = this.plots || [];
            let plots = this.plots.filter(p => !this.isSelectedPlot(p));
            plots.forEach(p => {
                let pixel = this.map.getPixelFromCoordinate(p.coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 7, pixel[1] - 22, pixel[0] + 7, pixel[1]]);
                }
                let labelItem = getPlotLabelItem(p);
                if (labelItem) {
                    if (p.cursor || p.cursor === 0) {
                        plotLabelItemsCursor.push(labelItem);
                    } else {
                        plotLabelItemsNormal.push(labelItem);
                    }
                }
            });

            let vesselLabelItems = [];
            this.vessels = this.vessels || [];
            let vessels = this.vessels.filter(v => !this.isSelectedVessel(v));
            vessels.forEach(v => {
                let pixel = this.map.getPixelFromCoordinate(v.coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 7, pixel[1] - 7, pixel[0] + 7, pixel[1] + 7]);
                }
                let labelItem = getLabelItem(v);
                if (labelItem) {
                    vesselLabelItems.push(labelItem);
                }
            });

            plotLabelItemsCursor = placeLabelItems(plotLabelItemsCursor, this.map, occupiedRects);
            plotLabelItemsNormal = placeLabelItems(plotLabelItemsNormal, this.map, occupiedRects);
            vesselLabelItems = placeLabelItems(vesselLabelItems, this.map, occupiedRects);

            let features = [];
            let cursorFeatures = [];
            plots.forEach(p => {
                if (p.cursor || p.cursor === 0) {
                    let labelItem = plotLabelItemsCursor.find(l => l.plot && l.id === p.id);
                    let direction = labelItem && labelItem.direction;
                    cursorFeatures.push(this.createPlotFeature(p, direction));
                } else {
                    let labelItem = plotLabelItemsNormal.find(l => l.plot && l.id === p.id);
                    let direction = labelItem && labelItem.direction;
                    features.push(this.createPlotFeature(p, direction));
                }
            });
            this.plotLayer.setSource(new VectorSource({features: features}));
            this.plotCursorLayer.setSource(new VectorSource({features: cursorFeatures}));
        },
        updateVesselRouteLayer() {
            let features = [];
            this.vesselRoute.forEach(p => {
                if (p.isStart) {
                    if (p.isError) {
                        features.push(this.createPortFeature(p, "image/marker_start_error_sprite.png"));
                    } else {
                        features.push(this.createPortFeature(p, "image/marker_start_sprite.png"));
                    }
                } else if (p.isTarget) {
                    if (p.isError) {
                        features.push(this.createPortFeature(p, "image/marker_target_error_sprite.png"));
                    } else {
                        features.push(this.createPortFeature(p, "image/marker_target_sprite.png"));
                    }
                } else if (!this.isSelectedPlot(p)) {
                    features.push(this.createPlotFeature(p));
                }
            });
            this.vesselWaypoints.forEach(p => {
                features.push(this.createPortFeature(p, "image/marker_waypoint_sprite.png"));
            })
            this.vesselRouteLayer.setSource(new VectorSource({features: features}));
            features = [];
            this.vesselRouteRects.forEach(item => {
                let coordinates = getRectCoordinates(item);
                let tooltipLines = [`名称：${item.name}`];
                if (item.river) {
                    tooltipLines.push(`河流：${item.river}`, `里程：${item.startCursor}~${item.endCursor}`);
                }
                let feature = new Feature({
                    geometry: new Polygon([coordinates]), routeRect: item, tooltip: tooltipLines.join("\n"),
                });
                let color = "magenta";
                feature.setStyle(new Style({
                    fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
                    stroke: new Stroke({color, width: 1}),
                    text: new Text({
                        text: item.name,
                        font: 'normal 12px sans-serif',
                        placement: 'line',
                        offsetX: 0,
                        offsetY: 0,
                        textAlign: "left",
                        textBaseline: "top",
                        fill: new Fill({color: color}),
                        stroke: new Stroke({color: "white", width: 3}),
                    }),
                }));
                features.push(feature);
            });
            this.vesselRouteRectLayer.setSource(new VectorSource({features: features}));
        },
        updateSelectedPlotLayer() {
            let features = [];
            if (this.selectedItem && this.selectedItem.class === "plot") {
                let coordinate = this.selectedItem.coordinate;
                let direction = labelDirections[0];
                features.push(createFeature(coordinate, selectedStyle));
                features.push(this.createPlotFeature(this.selectedItem));
                let occupiedRects = [];
                let pixel = this.map.getPixelFromCoordinate(coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 17, pixel[1] - 17, pixel[0] + 17, pixel[1] + 17]);
                }
                let labelItem = getPlotLabelItem(this.selectedItem, true);
                if (labelItem) {
                    occupiedRects.push(getLabelRect(labelItem, direction, this.map));
                }
                this.selectedOccupiedRects = occupiedRects;
            } else if (!this.selectedItem) {
                this.selectedOccupiedRects = [];
            }
            this.selectedPlotLayer.setSource(new VectorSource({features: features}));
        },
        createGroupFeature(g) {
            let tooltip = `${g.size}个标绘`;
            return createFeature(g.coordinate, createGroupStyle(g), {group: g, tooltip: tooltip});
        },
        createPlotFeature(p, direction) {
            let tooltipLines = [`名称：${p.name}`, `类型：${p.type}`];
            if (p.aliases.length > 0) {
                tooltipLines.push(`别名：${p.aliases.join()}`);
            }
            if (p.river) {
                tooltipLines.push(`河流：${p.river}`);
            }
            let hasCursor = (p.cursor || p.cursor === 0);
            if (hasCursor) {
                tooltipLines.push(`里程：${p.cursor}`);
            }
            if (p.extensionRiver) {
                tooltipLines.push(`推测河流：${p.extensionRiver}`);
            }
            let hasExtensionCursor = (p.extensionCursor || p.extensionCursor === 0);
            if (hasExtensionCursor) {
                tooltipLines.push(`推测里程：${p.extensionCursor}`);
            }
            if (p.extensionOrigin) {
                tooltipLines.push(`推测来源：${p.extensionOrigin}`);
            }
            if (p.extensionPort) {
                tooltipLines.push(`所属港口：${p.extensionPort}`);
            }
            if (p.note) {
                tooltipLines.push(`备注：${p.note}`);
            }
            return createFeature(p.coordinate, createPlotStyle(p, direction, hasCursor ? "#dff0d8" : null), {
                plot: p, tooltip: tooltipLines.join("\n")
            });
        },
        createPortFeature(p, imagePath) {
            return createFeature(p.coordinate, new Style({
                image: new Icon({src: imagePath, size: [40, 40], anchor: [0.5, 0.9]})
            }), {plot: p, tooltip: p.name, type: 'port'});
        },
        onPointerMove(e) {
            if (e.dragging) {
                return;
            }

            let cursor = "default";
            this.lastPointerPixel = this.map.getEventPixel(e.originalEvent);
            let feature = this.updateTooltip();
            if (feature) {
                cursor = feature.get("group") ? "pointer" : "default";
            }
            document.body.style.cursor = cursor;
        },
        updateTooltip() {
            if (this.lastPointerPixel) {
                let feature = this.getTooltipFeature(this.lastPointerPixel);
                if (feature) {
                    this.tooltip = feature.get('tooltip');
                    this.tooltipX = this.lastPointerPixel[0] + 7;
                    this.tooltipY = this.lastPointerPixel[1] + 20;
                } else if (this.tooltip) {
                    this.tooltip = "";
                }
                return feature;
            }
        },
        getTooltipFeature(pixel) {
            let feature = null;
            this.map.forEachFeatureAtPixel(pixel, function (x) {
                feature = feature || (x.get('tooltip') ? x : null);
            }, {hitTolerance: 5});
            return feature;
        },
        onClick(e) {
            if (e && e.pointerEvent && e.pointerEvent.button === 0) {
                let pixel = this.map.getEventPixel(e.originalEvent);
                let feature = this.getTooltipFeature(pixel);
                if (feature) {
                    let group = feature.get('group');
                    if (group) {
                        this.zoomIn(group.coordinate);
                    } else {
                        let plot = feature.get('plot');
                    }
                }
            }
        },
        zoomIn(center) {
            let view = this.map.getView();
            view.setZoom(view.getZoom() + 1);
            view.setCenter(center);
            setTimeout(() => this.updateTooltip(), 0);
        },
        searchClear() {
            this.searchInput = "";
            this.selectSearchResult(null);
            setTimeout(() => this.$refs.searchInput.focus());
        },
        async onKeywordChanged() {
            if (this.searchInput !== this.lastSearchInput) {
                this.lastSearchInput = this.searchInput;
                let searchInput = (this.searchInput || "").trim();
                let result = [];
                if (searchInput) {
                    try {
                        this.searching++;
                        result = await Plot.search(searchInput, {onlyPlots: true});
                    } finally {
                        this.searching--;
                    }
                    result.forEach(x => {
                        if (x.class === "vessel") {
                            x.displayName = vesselDisplayName(x);
                        } else {
                            if (x.category === "line") {
                                x.displayName = linePlotDisplayName(x);
                            } else if (x.category === "rect") {
                                x.displayName = rectPlotDisplayName(x);
                            } else {
                                x.displayName = plotDisplayName(x);
                            }
                        }
                    });
                }
                if (searchInput === this.searchInput || this.searchResultKey !== this.searchInput) {
                    this.searchResultKey = this.searchInput;
                    this.searchResult = result;
                }
            }
        },
        async selectPlot(id, noAction) {
            await this.selectSearchResult({class: "plot", id}, noAction);
            return !!this.selectedItem;
        },
        getStringName(name) {
            return (name || name === 0) ? name.toString() : "";
        },
        async selectSearchResult(item, noAction, noActionAfterViewPortSet) {
            let selected = null;
            if (item && item.class === "plot") {
                if (item.category === "line" || item.category === "rect") {
                    this.showSearchResult = false;
                    let coordinates = getRectCoordinates(item);
                    this.setViewport(coordinates, 16);
                    let type = this.markerTypes.find(x => x.category === item.category && x.name === item.type);
                    if (type && !type.value) {
                        type.value = true;
                        this.markerTypeChange(type);
                    }
                } else {
                    selected = await Plot.load(item.id);
                    if (selected) {
                        selected.class = "plot";
                        selected.displayName = plotDisplayName(selected);
                        this.showSearchResult = false;
                        this.searchInput = this.getStringName(selected.name);
                    }
                }
            }
            if (selected) {
                selected.keyword = item.keyword;
                selected.coordinate = fromLonLat([selected.lon, selected.lat]);
                this.selectedItem = selected;
                if (!noAction && !(noActionAfterViewPortSet && this.viewportSet)) {
                    let view = this.map.getView();
                    view.setCenter(selected.coordinate);
                }
            } else {
                this.selectedItem = null;
            }
            this.updateSelectedPlotLayer();
            this.updatePlotLayer();
        },
        setVesselRoute(vesselRoute, vesselRouteRects, vesselWaypoints) {
            if (this.map) {
                vesselRoute.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
                vesselWaypoints.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
                this.vesselRoute = vesselRoute;
                this.vesselRouteRects = vesselRouteRects;
                this.vesselWaypoints = vesselWaypoints;
                this.updateVesselRouteLayer();
                if (this.vesselRoute.length > 0) {
                    this.setViewport(this.vesselRoute.concat(this.vesselWaypoints).map(x => x.coordinate));
                }
                this.updatePlots();
            } else {
                setTimeout(() => this.setVesselRoute(vesselRoute, vesselRouteRects, vesselWaypoints), 100);
            }
        },
        setViewport(coordinates) {
            if (Array.isArray(coordinates) && coordinates.length > 0) {
                let size = this.getClientSize();
                let px = Math.max(50, Math.round(size[0] * 0.1));
                let py = Math.max(50, Math.round(size[1] * 0.1));
                this.map.getView().fit(olExtent.boundingExtent(coordinates), {
                    padding: [py, px, py, px],
                    maxZoom: 11
                });
                this.viewportSet = true;
            }
        },
        async load(type) {
            let plots = await Practice.load(type);
            plots.forEach((p, index) => {
                p.id = index + 1;
                p.coordinate = fromLonLat([p.lon, p.lat]);
                p.aliases = p.aliases || [];
                p.displayName = plotDisplayName(p);
            });
            this.setViewport(plots.map(x => x.coordinate));
            this.allPlots = plots;
            this.updatePlots();
        },
    },
    watch: {
        searchInput: _.debounce(function (val) {
            this.onKeywordChanged();
        }, 300),
        startText: _.debounce(function () {
            this.queryStartItems();
        }, 300),
        targetText: _.debounce(function () {
            this.queryTargetItems();
        }, 300),
    },
    async mounted() {
        $(document.body).css("overflow", "hidden");

        this.selectedOccupiedRects = [];
        this.plots = [];
        this.plotLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.plotCursorLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.groupLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.selectedPlotLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.vesselRouteLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.vesselRouteRectLayer = new VectorLayer({source: new VectorSource({features: []})});
        let center = fromLonLat([119.511392, 32.21177]);
        let zoom = 8;
        this.map = new OpenLayersMap({
            target: 'ol-map-container',
            interactions: defaultInteractions({
                altShiftDragRotate: false,
                pinchRotate: false,
                doubleClickZoom: false,
            }),
            controls: defaultControls().extend([
                new ScaleLineControl({units: 'metric'}),
                new MapTypeControl(),
                new RoadTypeControl(),
            ]),
            loadTilesWhileInteracting: true,
            view: new View({center, zoom, maxZoom: 19}),
            layers: getLayers().concat([
                this.vesselRouteRectLayer,
                this.plotLayer,
                this.plotCursorLayer,
                this.groupLayer,
                this.vesselRouteLayer,
                this.selectedPlotLayer,
            ]),
        });
        this.map.on("moveend", () => this.updatePlots());
        this.map.on("pointermove", e => this.onPointerMove(e));
        this.map.on('singleclick', e => this.onClick(e));
        this.map.getViewport().addEventListener('mouseout',
            () => this.helpTooltipElement && this.helpTooltipElement.classList.add('hidden')
        );

        this.mode = this.$route.query["mode"] || "";
        await initPlotTypes();
        let type = this.$route.query["type"] || "";
        await this.load(type);
    },
    async created() {
        this.allPlots = [];
        this.groups = [];
        this.plots = [];
        this.vesselRoute = [];
        this.vesselRouteRects = [];
        this.clickOutHandler = e => {
            let target = e.target || e.srcElement;
            const hasParent = function hasParent(node, parent) {
                while (node) {
                    if (node === parent) {
                        return true;
                    }
                    node = node.parentNode;
                }
                return false;
            };
            if (!hasParent(target, this.$refs.search)) {
                this.showSearchResult = false;
                this.$refs.searchInput.blur();
            }
            if (!hasParent(target, this.$refs.startRoute)) {
                this.showStartItems = false;
                this.$refs.routeStartInput.blur();
            }
            if (!hasParent(target, this.$refs.targetRoute)) {
                this.showTargetItems = false;
                this.$refs.routeTargetInput.blur();
            }
        };
        document.body.addEventListener("click", this.clickOutHandler);
        this.sizeHandler = () => this.mapHeight = this.getClientSize()[1] + "px";
        window.onresize = this.sizeHandler;
        this.sizeHandler();
    },
    destroyed() {
        $(document.body).css("overflow", "");
        document.body.removeEventListener("click", this.clickOutHandler);
    },
}
</script>

<style scoped>
.map-menu-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 10px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
}

.search-input {
    width: 30%;
    max-width: 500px;
    position: relative;
}

.route-panel {
    flex: 1;
    margin-left: 1rem;
    width: calc(37% - 1rem);
    background-color: transparent;
    height: 38px;
    line-height: 38px;
    display: flex;
}

.route-dropdown {
}

.route-input {
}

.search-input input {
    padding-left: 38px;
    padding-right: 30px;
}

.no-pointer-events {
    pointer-events: none;
}

.search-input .search-icon {
    position: absolute;
    left: 9px;
    top: 9px;
    pointer-events: none;
}

.search-input .clear-icon {
    position: absolute;
    right: 9px;
    top: 9px;
}

.config-buttons {
    margin-left: 1rem;
    width: 170px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.config-buttons > * + * {
    margin-left: 0.5rem;
}

.marker-dropdown {
    width: 170px;
}

.map-tooltip {
    position: absolute;
    top: 0;
    left: 0;
    padding: .25em .5em;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    z-index: 100;
}

@media (max-width: 1200px) {
    .route-panel {
        width: calc(50% - 1rem);
    }

    .config-buttons {
        width: 100%;
        margin-left: 0;
        margin-top: .5rem;
    }
}

@media (max-width: 768px) {
    .search-input {
        width: 100%;
        max-width: 100%;
    }

    .route-panel {
        width: 100%;
        margin-left: 0;
        margin-top: .5rem;
    }

    .config-buttons {
        width: 100%;
        margin-left: 0;
        margin-top: .5rem;
    }
}
</style>

<style>
.ol-map-type {
    bottom: 10px;
    left: 10px;
}

.ol-road-type {
    bottom: 10px;
    left: 160px;
}

.ol-control.ol-map-type, .ol-control.ol-road-type {
    display: flex;
}

.ol-control.ol-map-type button, .ol-control.ol-road-type button {
    width: auto;
    height: auto;
    line-height: normal;
    font-size: 18px;
    font-weight: normal;
    padding: 5px 10px;
}

.ol-control.ol-map-type button.selected, .ol-control.ol-road-type button.selected {
    text-decoration: none;
    background-color: rgba(0, 60, 136, .8);
}

.ol-scale-line {
    top: auto;
    left: auto;
    right: 10px;
    bottom: 10px;
}

.ol-zoom {
    left: auto;
    top: auto;
    right: 10px;
    bottom: 40px;
}

.ol-zoom button {
    font-size: 1.5rem;
}

@media (max-width: 512px) {
    .ol-road-type {
        bottom: 55px;
        left: 10px;
    }
}

.ol-tooltip {
    position: relative;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.7;
    white-space: nowrap;
    font-size: 12px;
}

.ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
}

.ol-tooltip-static {
    background-color: orangered;
    color: white;
    border: 1px solid white;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
}

.ol-tooltip-static:before {
    border-top-color: orangered;
}

.ol-tooltip-static-close-btn {
    font-size: 1.4rem;
    line-height: 0.8rem;
    height: 1rem;
    cursor: pointer;
}

.ol-tooltip-static-close-btn:hover {
}

.ol-tooltip-static-close-btn:active {
}

.ol-event-transparent {
    pointer-events: none;
}

.ol-event-solid {
    pointer-events: auto;
}

.ol-circle {
    background: transparent;
    width: 10px;
    height: 10px;
    border: 1px solid black;
    border-radius: 5px;
}
</style>
